// Sessão 7B/7C: partesApi (buscar) + propostasApi adicionados.
import axios, { AxiosInstance } from 'axios';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, loginRequest } from '../auth/authConfig';
import type { Operacao, ColunasKanban, PropostaBancaria, PropostaBancariaPayload, BancoPropostaBancaria, DivergenciaConector, ResultadoListaSemCcv, EventoOperacao, CriticaCCV, ChecklistItem, Participante, PapelParticipante, PendenciaInformacao, Ocupacao, MovimentacaoProposta, MatrizTransicaoProposta, FilaConsultaCadastralResposta, ItemFilaHomologacao, VeredictoHomologacao, FaixaEnquadramento, TemplateWhatsapp, ComentarioOperacao, UsuarioMencionavel, Notificacao } from '../types';

// Sessão 4: baseURL inclui /api para corresponder ao prefixo do backend
const API_BASE = (import.meta.env.VITE_API_URL ?? 'http://localhost:3001') + '/api';

// Singleton MSAL para o interceptor (fora do contexto React)
let _msalInstance: PublicClientApplication | null = null;
export function setMsalInstance(instance: PublicClientApplication) {
  _msalInstance = instance;
}

const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// ── Interceptor: injeta id_token Azure AD em cada requisição ─────────────────
// Opção A (Sessão 4): enviamos o id_token, não o accessToken.
// O id_token tem audience = CLIENT_ID e pode ser validado via JWKS da Microsoft
// sem precisar expor a API no Azure AD (tarefa da Sessão 5+).
api.interceptors.request.use(async (config) => {
  if (!_msalInstance) return config;
  const accounts = _msalInstance.getAllAccounts();
  if (!accounts.length) return config;

  try {
    const result = await _msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });
    // id_token: JWT com aud=CLIENT_ID - validável pelo backend via JWKS
    if (result.idToken) {
      config.headers.Authorization = `Bearer ${result.idToken}`;
    }
  } catch {
    // Token expirado - força re-login
    await _msalInstance.acquireTokenRedirect(loginRequest);
  }
  return config;
});

// ── Endpoints de Operações ───────────────────────────────────────────────────

export const operacoesApi = {
  /** Busca todas as operações agrupadas por esteira */
  getKanban: async (): Promise<ColunasKanban> => {
    const { data } = await api.get<Operacao[]>('/operacoes');
    return agruparPorEsteira(data);
  },

  /** Busca detalhes completos de uma operação */
  getById: async (id: string): Promise<Operacao> => {
    const { data } = await api.get<Operacao>(`/operacoes/${id}`);
    return data;
  },

  /** Exclui a operação definitivamente (hard delete; ADMIN only). Exige confirmação = numero. */
  excluir: async (id: string, confirmacao: string): Promise<{ ok: boolean; numero: string }> => {
    const { data } = await api.delete<{ ok: boolean; numero: string }>(`/operacoes/${id}`, { data: { confirmacao } });
    return data;
  },

  /** Move operação para outra esteira via drag-and-drop */
  moverEsteira: async (id: string, esteira: number): Promise<Operacao> => {
    const { data } = await api.patch<Operacao>(`/operacoes/${id}/esteira`, { esteira });
    return data;
  },

  /** Congela a operação com motivo */
  congelar: async (
    id: string,
    payload: { motivo: string; prazoRetorno?: string }
  ): Promise<Operacao> => {
    const { data } = await api.patch<Operacao>(`/operacoes/${id}/congelar`, payload);
    return data;
  },

  /** Descongela a operação */
  descongelar: async (id: string): Promise<Operacao> => {
    const { data } = await api.patch<Operacao>(`/operacoes/${id}/descongelar`);
    return data;
  },

  /** Toggle clienteConduz na Esteira 4 */
  toggleClienteConduz: async (id: string, clienteConduz: boolean): Promise<Operacao> => {
    const { data } = await api.patch<Operacao>(`/operacoes/${id}/cliente-conduz`, { clienteConduz });
    return data;
  },

  /** Write-back CRM → iSolve (/detalhes/atualizar). Gate no backend (nasce OFF). */
  refletirNoIsolve: async (
    id: string,
    payload: { escopo: 'PARTE' | 'IMOVEL' | 'OPERACAO' | 'TUDO'; parteId?: string },
  ): Promise<{ ok: boolean; motivo?: string; camposAplicados?: number; gateDesligado?: boolean }> => {
    const { data } = await api.post(`/operacoes/${id}/isolve/refletir`, payload);
    return data;
  },

  /** Recoleta sob demanda: diff CRM × iSolve (só leitura no iSolve). */
  recoletarIsolve: async (
    id: string,
  ): Promise<{ ok: boolean; itens: ItemDiffRecoleta[]; motivo?: string; semIdCstimob?: boolean }> => {
    const { data } = await api.get(`/operacoes/${id}/recoletar-isolve`);
    return data;
  },

  /** Aplica os itens aprovados da recoleta (recomputa o diff no backend). */
  aplicarRecoletaIsolve: async (
    id: string,
    chaves: string[],
  ): Promise<{ ok: boolean; aplicados: number; motivo?: string }> => {
    const { data } = await api.post(`/operacoes/${id}/recoletar-isolve/aplicar`, { chaves });
    return data;
  },

  /** Move operação para outro sub-status dentro da esteira (Sessão 5) */
  moverSubStatus: async (
    id: string,
    subStatus: number,
    nomeAnterior?: string,
    nomeNovo?: string,
  ): Promise<Operacao> => {
    const { data } = await api.patch<Operacao>(`/operacoes/${id}/sub-status`, {
      subStatus,
      nomeAnterior,
      nomeNovo,
    });
    return data;
  },

  /** Busca operações de uma esteira específica para o sub-board (Sessão 5) */
  getPorEsteira: async (esteira: number): Promise<Operacao[]> => {
    const { data } = await api.get<Operacao[]>(`/operacoes?esteira=${esteira}`);
    return data;
  },

  /** SPEC_LAYOUT_UX_V1 (E1) - cruza a fronteira interna ENTRADA↔CREDITO dentro
   *  do sub-board mesclado do bucket "Crédito" (macroFase+subStatus explícitos,
   *  diferente de moverEsteira que sempre reseta para a 1ª etapa). */
  moverEsteiraInterna: async (
    id: string,
    macroFase: 'ENTRADA' | 'CREDITO',
    subStatus: number,
  ): Promise<Operacao> => {
    const { data } = await api.patch<Operacao>(`/operacoes/${id}/esteira-interna`, { macroFase, subStatus });
    return data;
  },

  /** Cria nova operação - pasta SharePoint criada automaticamente se nomeComprador+documentoComprador forem informados (Sessão 6).
   *  Sessão 7A: backend agora cria Parte (comprador) + Imovel; valorImovel em R$ (número). */
  criar: async (payload: {
    origem: 'QUINTO_ANDAR' | 'S_QUATRO' | 'VAREJO';
    nomeComprador?: string;
    documentoComprador?: string;
    valorImovel?: number;
    executivoNegociosId?: string;
    // OTMC-* (07/09/2026) - vincula manualmente ao idCstimob (solicitacaoId) do iSolve;
    // o Sincronizar completa CCV/proposta ao encontrar por esse idCstimob.
    idCstimob?: number;
    // SPEC_LAYOUT_UX_V1 (E4) - 4 campos obrigatórios do intake (quando há comprador)
    estadoCivil?: string;
    ocupacaoId?: string;
    condicaoImovel?: 'NOVO' | 'USADO';
    percentualComposicaoRenda?: number;
  }): Promise<Operacao> => {
    const { data } = await api.post<Operacao>('/operacoes', payload);
    return data;
  },

  /** SPEC S1 (E3) - feed de eventos paginado (alimenta a aba Timeline) */
  getEventos: async (id: string, cursor?: string): Promise<{ itens: EventoOperacao[]; proximoCursor: string | null }> => {
    const { data } = await api.get(`/operacoes/${id}/eventos`, { params: cursor ? { cursor } : undefined });
    return data;
  },

  /** SPEC S1 (E3) - marca "visto" do usuário logado; zera a bandeirinha ao abrir o modal */
  marcarVisto: async (id: string): Promise<{ vistoEm: string }> => {
    const { data } = await api.post(`/operacoes/${id}/visto`);
    return data;
  },

  /** SPEC S2 (E2/E4) - reconcilia pasta↔índice (vigia) e, em seguida, tria os
   *  manuais pendentes (mesmos gatilhos). Chamado em background ao abrir a aba
   *  Docs e sob demanda pelo botão "Vigiar pasta". */
  vigiar: async (id: string): Promise<{
    vigia: { novos: number; ausentes: number; reaparecidos: number; renomeados: number; ancorados: number };
    triagem: { triados: number; manuais: number; tetoAtingido: boolean };
  }> => {
    const { data } = await api.post(`/operacoes/${id}/vigiar`);
    return data;
  },

  /** SPEC S3 (E4) - última crítica de CCV (painel "Crítica do CCV" na aba Docs) */
  getCriticaCcv: async (id: string): Promise<CriticaCCV | null> => {
    const { data } = await api.get(`/operacoes/${id}/critica-ccv`);
    return data;
  },

  /** SPEC S3 (E4) - botão "Criticar CCV". Sempre re-executa (re-crítica explícita);
   *  só abre itens na fila de Divergências com o GATE E6 (CRITICA_HABILITADA) aberto. */
  criticarCcv: async (id: string): Promise<{
    status: 'SEM_CCV' | 'GATE_FECHADO' | 'JA_CRITICADA' | 'CONCLUIDA';
    criticaId?: string;
    statusGeral?: CriticaCCV['statusGeral'];
    divergencias?: number;
    naoCadastrados?: number;
    baixaConfianca?: number;
  }> => {
    const { data } = await api.post(`/operacoes/${id}/criticar-ccv`);
    return data;
  },

  /** SPEC_FILA_CONSULTA_CADASTRAL (E5) - fila de consultas SICAQ por personagem.
   *  X4-b (E5-bis) - retorno agora inclui documentosSemExtracao (sinal do "Reextrair"). */
  getFilaConsultaCadastral: async (id: string): Promise<FilaConsultaCadastralResposta> => {
    const { data } = await api.get(`/operacoes/${id}/consultas-cadastrais`);
    return data;
  },

  /** X4-a (15/07/2026, E5-bis) - botão "Reprocessar triagem" (aba Docs). Só se aplica
   *  a Documento em TRIAGEM_MANUAL por falha de TRANSPORTE (doc.falhaTransporte). */
  reprocessarTriagem: async (operacaoId: string, documentoId: string): Promise<unknown> => {
    const { data } = await api.post(`/operacoes/${operacaoId}/documentos/${documentoId}/reprocessar-triagem`);
    return data;
  },

  /** X4-b (15/07/2026, E5-bis) - botão "Reextrair" (fila de Consultas). Trava: 409 se
   *  já existir ConsultaCadastral pra este documento. */
  reextrairConsultaCadastral: async (operacaoId: string, documentoId: string): Promise<unknown> => {
    const { data } = await api.post(`/operacoes/${operacaoId}/documentos/${documentoId}/reextrair-consulta-cadastral`);
    return data;
  },

  /** SPEC_D (E3/E6) - itens do Checklist D. Chamado ao abrir a aba (gatilho de resolução). */
  getChecklist: async (id: string): Promise<{ resolucao: { executado: boolean; semPrograma: boolean }; itens: ChecklistItem[] }> => {
    const { data } = await api.get(`/operacoes/${id}/checklist`);
    return data;
  },

  /** SPEC_D (E3/E6) - botão "Reprocessar checklist". */
  reprocessarChecklist: async (id: string): Promise<{ itensCriadosOuAtualizados: number }> => {
    const { data } = await api.post(`/operacoes/${id}/reprocessar-checklist`);
    return data;
  },

  /** SPEC_D (E6) - participantes (N:N operação×parte, papel + composição de renda). */
  getParticipantes: async (id: string): Promise<Participante[]> => {
    const { data } = await api.get(`/operacoes/${id}/participantes`);
    return data;
  },

  criarParticipante: async (id: string, dados: { parteId: string; papel: PapelParticipante; percentualComposicaoRenda?: number | null }): Promise<Participante> => {
    const { data } = await api.post(`/operacoes/${id}/participantes`, dados);
    return data;
  },

  atualizarParticipante: async (id: string, participanteId: string, dados: Partial<{ papel: PapelParticipante; percentualComposicaoRenda: number | null }>): Promise<Participante> => {
    const { data } = await api.patch(`/operacoes/${id}/participantes/${participanteId}`, dados);
    return data;
  },

  removerParticipante: async (id: string, participanteId: string): Promise<void> => {
    await api.delete(`/operacoes/${id}/participantes/${participanteId}`);
  },

  /** SPEC_LAYOUT_UX_V1 (E5) - pendências de informação abertas da operação. */
  getPendenciasInformacao: async (id: string): Promise<PendenciaInformacao[]> => {
    const { data } = await api.get(`/operacoes/${id}/pendencias-informacao`);
    return data;
  },

  /** Informa o dado faltante - grava no campo real e recalcula o checklist. */
  resolverPendenciaInformacao: async (id: string, pendenciaId: string, valor: string | number | boolean): Promise<void> => {
    await api.post(`/operacoes/${id}/pendencias-informacao/${pendenciaId}/resolver`, { valor });
  },

  /** SPEC_CHECKLIST_INTELIGENTE_V1_1 (E3) - registra o resultado de uma checagem externa (balde B). */
  registrarChecagemExterna: async (id: string, itemId: string, dados: { veredito: 'OK' | 'RESTRICAO' | 'OBSERVACAO'; observacao?: string }): Promise<void> => {
    await api.post(`/operacoes/${id}/checklist/${itemId}/checagem-externa`, dados);
  },
};

// ── Partes ────────────────────────────────────────────────────────────────────
// Sessão 7B: busca CPF/CNPJ para alerta não-bloqueante no formulário de criação

// SPEC_LAYOUT_UX_V1 (E6) - Contato como entidade (fundação RACI, sem matriz)
export interface ContatoParte {
  id: string;
  tipo: string; // 'email' | 'telefone' | 'whatsapp'
  valor: string;
  principal: boolean;
  origem: 'ISOLVE' | 'CCV' | 'MANUAL';
  createdAt: string;
}

// Recoleta sob demanda do iSolve (iSolve → CRM) - item do diff.
export interface ItemDiffRecoleta {
  chave: string;
  entidade: 'Parte' | 'Imovel' | 'Operacao';
  entidadeId: string;
  referencia: string;
  campo: string;
  valorAtual: unknown;
  valorNovo: unknown;
  categoria: 'NOVO' | 'ALTERADO';
  parteNova?: boolean;
  papel?: 'comprador' | 'vendedor';
  cpfChave?: string;
}

export interface ParteDetalhe {
  id: string;
  nome: string;
  tipo: 'PF' | 'PJ';
  cpf?: string;
  cnpj?: string;
  estadoCivil?: string;
  estrangeiro: boolean;
  usaFgts: boolean;
  email?: string;
  telefone?: string;
  contatos: ContatoParte[];

  // Identidade civil
  rg?: string;
  dataNascimento?: string;
  sexo?: string;
  nomeMae?: string;
  nomePai?: string;
  nacionalidade?: string;
  naturalidade?: string;
  ufNascimento?: string;
  grauInstrucao?: string;
  estadoCivilDetalhado?: string;
  estadoCivilCurado?: 'SOLTEIRO' | 'CASADO' | 'DIVORCIADO' | 'VIUVO' | 'UNIAO_ESTAVEL' | 'SEPARADO_FATO' | 'DIVORCIADO_SEM_PARTILHA' | 'VIUVO_INVENTARIO_ABERTO';

  // Documento
  tipoDocumento?: string;
  numeroDocumento?: string;
  orgaoExpedidorDoc?: string;
  dataEmissaoDoc?: string;

  // Endereço
  enderecoLogradouro?: string;
  enderecoNumero?: string;
  enderecoComplemento?: string;
  enderecoBairro?: string;
  enderecoCidade?: string;
  enderecoCep?: string;
  enderecoUf?: string;
  tempoResidencia?: string;
  tipoResidencia?: string;

  // Profissional / renda
  profissao?: string;
  cargo?: string;
  tipoContratacao?: string;
  dataAdmissao?: string;
  nomeEmpresa?: string;
  cnpjEmpresa?: string;
  rendaMensal?: number | null;
  outraRenda?: number | null;
  origemOutraRenda?: string;
  rendaFamiliar?: number | null;
  socioEmpresa?: string;
  cnpjSocioProprietario?: string;
  patrimonio?: number | null;

  // Cônjuge / união
  uniaoEstavel?: boolean | null;
  regimeCasamento?: string;
  dataUniaoOuCasamento?: string;
  conjugeNome?: string;
  conjugeCpf?: string;
  conjugeEmail?: string;

  // PEP / compliance
  pessoaPoliticamenteExposta?: boolean | null;
  cpfPep?: string;
  nomePep?: string;
  profissaoPep?: string;
  paisResidenciaPep?: string;
  dsAmericano?: string;

  // Bancos
  dadosBancariosComprador?: unknown;
  correntistaBradesco?: boolean | null;

  // INSS
  nrInss?: string;
  rendaInssAnual?: number | null;

  // Imóvel
  imovelFinanciadoSfh?: string;
  imovelMunicipioResidente?: string;
  imovelOndeExerceOcupacao?: string;
  imovelMunicipioAdquirir?: string;
  posicaoIsolve?: number | null;
}

export const partesApi = {
  buscar: async (documento: string): Promise<{ encontrado: boolean; nome?: string; tipo?: 'PF' | 'PJ' }> => {
    const { data } = await api.get('/partes/buscar', { params: { documento } });
    return data;
  },

  getById: async (id: string): Promise<ParteDetalhe> => {
    const { data } = await api.get<ParteDetalhe>(`/partes/${id}`);
    return data;
  },

  atualizar: async (
    id: string,
    payload: Partial<ParteDetalhe>,
  ): Promise<ParteDetalhe> => {
    const { data } = await api.patch<ParteDetalhe>(`/partes/${id}`, payload);
    return data;
  },

  /** SPEC_LAYOUT_UX_V1 (E6) - adiciona contato manual no Drawer da Parte (sempre origem=MANUAL) */
  adicionarContato: async (
    id: string,
    dados: { tipo: 'email' | 'telefone' | 'whatsapp'; valor: string; principal?: boolean },
  ): Promise<{ resultado: string; contatos: ContatoParte[] }> => {
    const { data } = await api.post(`/partes/${id}/contatos`, dados);
    return data;
  },
};

// ── Propostas Bancárias ───────────────────────────────────────────────────────
// Sessão 7C: N propostas por operação (CAIXA + Itaú + Santander simultâneos)

export const propostasApi = {
  /** Lista todas as propostas de uma operação */
  listar: async (operacaoId: string): Promise<PropostaBancaria[]> => {
    const { data } = await api.get<PropostaBancaria[]>(`/operacoes/${operacaoId}/propostas`);
    return data;
  },

  /** Cria uma nova proposta para a operação */
  criar: async (
    operacaoId: string,
    payload: PropostaBancariaPayload
  ): Promise<PropostaBancaria> => {
    const { data } = await api.post<PropostaBancaria>(`/operacoes/${operacaoId}/propostas`, payload);
    return data;
  },

  /** Atualiza campos de uma proposta */
  atualizar: async (
    operacaoId: string,
    propostaId: string,
    payload: PropostaBancariaPayload
  ): Promise<PropostaBancaria> => {
    const { data } = await api.patch<PropostaBancaria>(
      `/operacoes/${operacaoId}/propostas/${propostaId}`,
      payload
    );
    return data;
  },

  /** Remove uma proposta */
  remover: async (operacaoId: string, propostaId: string): Promise<void> => {
    await api.delete(`/operacoes/${operacaoId}/propostas/${propostaId}`);
  },

  /** SPEC_LAYOUT_UX_V1 (E1) - seleciona o banco vencedor (etapa "Seleção de
   *  Banco", última da esteira de Crédito). Pré-requisito do gate de transição
   *  para Jurídico (GATE_SELECAO_BANCO_HABILITADO). */
  selecionar: async (operacaoId: string, propostaId: string): Promise<{ bancoSelecionadoId: string; bancoFinanciador: string }> => {
    const { data } = await api.post(`/operacoes/${operacaoId}/propostas/${propostaId}/selecionar`);
    return data;
  },

  /** SPEC_ESTACAO1 (E6) - matriz de transição de status, fonte única do
   *  backend (transicaoProposta.service.ts) - nunca duplicar a regra na UI. */
  buscarMatrizTransicao: async (operacaoId: string): Promise<MatrizTransicaoProposta> => {
    const { data } = await api.get<MatrizTransicaoProposta>(`/operacoes/${operacaoId}/propostas/matriz-transicao`);
    return data;
  },

  /** SPEC_ESTACAO1 (E6) - transiciona o status via transicionarStatusProposta
   *  (matriz + histórico + bloqueio por pendência no backend). `motivo`
   *  obrigatório nas transições de retorno - validado também no backend. */
  transicionar: async (
    operacaoId: string,
    propostaId: string,
    status: PropostaBancaria['status'],
    motivo?: string,
  ): Promise<PropostaBancaria> => {
    const { data } = await api.patch<PropostaBancaria>(
      `/operacoes/${operacaoId}/propostas/${propostaId}`,
      { status, motivo },
    );
    return data;
  },

  /** SPEC_ESTACAO1 (E6) - histórico de transições desta proposta (E2), mais recente primeiro. */
  buscarMovimentacoes: async (operacaoId: string, propostaId: string): Promise<MovimentacaoProposta[]> => {
    const { data } = await api.get<MovimentacaoProposta[]>(`/operacoes/${operacaoId}/propostas/${propostaId}/movimentacoes`);
    return data;
  },
};

// ── Fila de Divergências iSolve (SPEC_CONECTOR_INCREMENTAL v1.1) ─────────────

export const divergenciasApi = {
  listar: async (params?: {
    tipo?: string;
    status?: string;
    de?: string;
    ate?: string;
    operacaoId?: string;
    esteira?: number;
  }): Promise<DivergenciaConector[]> => {
    const { data } = await api.get<DivergenciaConector[]>('/divergencias', { params });
    return data;
  },

  aprovar: async (id: string, observacao?: string): Promise<DivergenciaConector> => {
    const { data } = await api.patch<DivergenciaConector>(`/divergencias/${id}`, {
      acao: 'APROVAR',
      observacao,
    });
    return data;
  },

  rejeitar: async (id: string, observacao?: string): Promise<DivergenciaConector> => {
    const { data } = await api.patch<DivergenciaConector>(`/divergencias/${id}`, {
      acao: 'REJEITAR',
      observacao,
    });
    return data;
  },

  contarPendentes: async (): Promise<number> => {
    const { data } = await api.get<{ pendentes: number }>('/divergencias/pendentes/count');
    return data.pendentes;
  },
};

// ── Fila de Homologação de Enquadramento (SPEC_BLOCO6, 19/07/2026) ───────────

export const homologacaoEnquadramentoApi = {
  listar: async (status?: 'PENDENTE' | 'DECIDIDO'): Promise<ItemFilaHomologacao[]> => {
    const { data } = await api.get<ItemFilaHomologacao[]>('/homologacao-enquadramento', { params: { status } });
    return data;
  },

  registrarVeredito: async (
    id: string,
    dados: { veredicto: VeredictoHomologacao; faixaAjustada?: FaixaEnquadramento; justificativa?: string },
  ): Promise<ItemFilaHomologacao> => {
    const { data } = await api.post<ItemFilaHomologacao>(`/homologacao-enquadramento/${id}/veredito`, dados);
    return data;
  },
};

// ── Etapas de Sub-Esteira (Alicerce Onda 1, 24/06/2026) ──────────────────────

export interface EtapaSubEsteira {
  id: string;
  ordem: number;
  label: string;
  obrigatoria: boolean;
  terminal: boolean;
}

// Catálogo completo (admin): inclui inativas + contagem de uso.
export interface EtapaSubEsteiraAdmin {
  id: string;
  macroFase: string;
  ordem: number;
  label: string;
  obrigatoria: boolean;
  terminal: boolean;
  ativa: boolean;
  operacoesTotal: number;
  operacoesVivas: number;
}

export const etapasSubEsteiraApi = {
  listar: async (macroFase: string): Promise<EtapaSubEsteira[]> => {
    const { data } = await api.get<EtapaSubEsteira[]>(`/etapas-sub-esteira/${macroFase}`);
    return data;
  },

  // ── Gerenciador (ADMIN) ────────────────────────────────────────────────────
  listarAdmin: async (macroFase: string): Promise<EtapaSubEsteiraAdmin[]> => {
    const { data } = await api.get<EtapaSubEsteiraAdmin[]>(`/etapas-sub-esteira/admin/${macroFase}`);
    return data;
  },
  criar: async (input: {
    macroFase: string;
    label: string;
    obrigatoria?: boolean;
    terminal?: boolean;
  }): Promise<EtapaSubEsteiraAdmin> => {
    const { data } = await api.post<EtapaSubEsteiraAdmin>('/etapas-sub-esteira', input);
    return data;
  },
  editar: async (
    id: string,
    input: { label?: string; obrigatoria?: boolean; terminal?: boolean },
  ): Promise<EtapaSubEsteiraAdmin> => {
    const { data } = await api.patch<EtapaSubEsteiraAdmin>(`/etapas-sub-esteira/${id}`, input);
    return data;
  },
  definirAtiva: async (id: string, ativa: boolean): Promise<EtapaSubEsteiraAdmin> => {
    const { data } = await api.patch<EtapaSubEsteiraAdmin>(`/etapas-sub-esteira/${id}/ativa`, { ativa });
    return data;
  },
  reordenar: async (macroFase: string, ids: string[]): Promise<EtapaSubEsteiraAdmin[]> => {
    const { data } = await api.put<EtapaSubEsteiraAdmin[]>('/etapas-sub-esteira/reordenar', { macroFase, ids });
    return data;
  },
  remover: async (id: string): Promise<EtapaSubEsteiraAdmin[]> => {
    const { data } = await api.delete<EtapaSubEsteiraAdmin[]>(`/etapas-sub-esteira/${id}`);
    return data;
  },
};

// ── iSolve - sessão + ações ───────────────────────────────────────────────────

export type AlertaSessaoISolve = 'ATIVA' | 'EXPIRANDO' | 'SUSPEITA' | 'EXPIRADA' | 'SEM_SESSAO';
export type ErroISolve = 'SESSAO_EXPIRADA' | 'SESSAO_SUSPEITA' | 'SEM_SESSAO' | 'GATEWAY_INSTAVEL';

export interface SessaoISolveStatus {
  status: string;
  expEm: string | null;
  expiraEmDias: number | null;
  alerta: AlertaSessaoISolve;
}

export interface ErroISolveResponse {
  erro: ErroISolve;
}

export interface ResultadoSincronizacao {
  status: 'ok' | 'parcial' | 'sem_watermark';
  novasImportadas: number;
  atualizadas: number;
  divergenciasEnfileiradas: number;
  falhas: Array<{ offerId: string; motivo: string }>;
  reconciliacao: { faltantesDetectadas: number };
  // ── ADITIVOS (SPEC 5, 08/07/2026) - telemetria completa do scan de CCV sem
  // proposta registrada (substitui os 2 campos soltos da SPEC 3B).
  ccvSemProposta: {
    paginasLidas: number;
    itensVistos: number;
    itensComCcv: number;
    esqueletosCriados: number;
    recusadasP5a: Array<{ idCstimob: string; clienteMascarado: string; motivo: string }>;
    puladosPorDedup: number;
    tetoAtingido: boolean;
  };
  // ── ADITIVO (MET-017/MET-018, 09/07/2026) - quantas vezes o Sincronizar rodou
  // hoje (execuções, não criações - ver RECONCILIACAO_INSTRUMENTACAO_09-07.md).
  execucoesHoje: number;
}

export const isolveApi = {
  getSessaoMinha: async (): Promise<SessaoISolveStatus> => {
    const { data } = await api.get<SessaoISolveStatus>('/isolve/sessao/minha');
    return data;
  },

  sincronizar: async (): Promise<ResultadoSincronizacao> => {
    const { data } = await api.post<ResultadoSincronizacao>('/isolve/importar/incremental');
    return data;
  },

  ultimaSync: async (): Promise<{ quando: string; status: string } | null> => {
    const { data } = await api.get('/isolve/ultima-sincronizacao');
    return data;
  },

  importarMassa: async (): Promise<unknown> => {
    const { data } = await api.post('/isolve/importar/massa');
    return data;
  },

  // ── Buscar no iSolve - universo de OPERAÇÕES, exceção pré-CCV (SPEC 3.1) ──
  listarSemCcv: async (): Promise<ResultadoListaSemCcv> => {
    const { data } = await api.get<ResultadoListaSemCcv>('/isolve/sem-ccv');
    return data;
  },

  importarOperacaoPreProposta: async (idCstimob: string): Promise<{ ok: true; operacaoId: string } | { ok: false; motivo: string }> => {
    try {
      const { data } = await api.post('/isolve/sem-ccv/importar', { idCstimob });
      return data;
    } catch (e: any) {
      if (e?.response?.status === 422 && e.response.data) return e.response.data;
      throw e;
    }
  },

  // Enriquecimento em 2º plano do valor de financiamento desejado - REFINO 02/07/2026 item 2
  buscarValorFinanciamento: async (idCstimob: string): Promise<number | null> => {
    const { data } = await api.get<{ valor: number | null }>(`/isolve/sem-ccv/${idCstimob}/valor`);
    return data.valor;
  },

  // E4(ii) SPEC B - botão "Trazer documentos do iSolve" (por operação, sob demanda)
  trazerDocumentosOperacao: async (operacaoId: string): Promise<{ ok: boolean; ingeridos: number; gateDesligado?: boolean; motivo?: string; encontrados?: number; jaExistiam?: number; falhasDownload?: number; falhasUpload?: number; falhasListagem?: number; syncOk?: boolean }> => {
    const { data } = await api.post<{ ok: boolean; ingeridos: number; gateDesligado?: boolean; motivo?: string; encontrados?: number; jaExistiam?: number; falhasDownload?: number; falhasUpload?: number; falhasListagem?: number; syncOk?: boolean }>(`/isolve/operacoes/${operacaoId}/documentos`);
    return data;
  },
};

// ── Novas Operações (OTMC-*, 07/09/2026) - fila de candidatas a criar ───────
export type EstadoCandidata = 'PENDENTE' | 'APROVADO' | 'REJEITADO';
export type CategoriaCandidata = 'ELEGIVEL' | 'CONCLUIDA_ISOLVE' | 'MORTA' | null;

export interface CandidataNovaOperacao {
  id: string;
  idCstimob: string;
  cliente: string | null;
  criadoEm: string;
  status: EstadoCandidata;
  categoria: CategoriaCandidata;
  idSituacao: number | null;
  situacao: string | null;
  dataAssinaturaCCV: string | null;
  dataCadastro: string | null;
  valor: number | null;
  parceiro: string | null;
  jaNoCrm: boolean;
  operacaoId: string | null;
  atualizadoEmMeta: string | null;
}

export interface ContadoresNovasOperacoes {
  total: number;
  pendentes: number;
  criadas: number;
  concluidas: number;
  jaNoCrm: number;
  elegiveis: number;
}

export interface FiltrosNovasOperacoes {
  status?: EstadoCandidata;
  categoria?: string;
  busca?: string;
}

export interface ResultadoCriacao {
  idCstimob: string;
  ok: boolean;
  motivo?: string;
  aviso?: string;
}

export const novasOperacoesApi = {
  listar: async (filtros: FiltrosNovasOperacoes = {}): Promise<{ itens: CandidataNovaOperacao[]; contadores: ContadoresNovasOperacoes }> => {
    const { data } = await api.get<{ itens: CandidataNovaOperacao[]; contadores: ContadoresNovasOperacoes }>('/isolve/novas-operacoes', { params: filtros });
    return data;
  },

  criar: async (idCstimobs: string[]): Promise<{ resultados: ResultadoCriacao[] }> => {
    const { data } = await api.post<{ resultados: ResultadoCriacao[] }>('/isolve/novas-operacoes/criar', { idCstimobs });
    return data;
  },

  concluir: async (idCstimobs: string[]): Promise<{ ok: boolean }> => {
    const { data } = await api.post<{ ok: boolean }>('/isolve/novas-operacoes/concluir', { idCstimobs });
    return data;
  },

  reabrir: async (idCstimobs: string[]): Promise<{ ok: boolean }> => {
    const { data } = await api.post<{ ok: boolean }>('/isolve/novas-operacoes/reabrir', { idCstimobs });
    return data;
  },
};

// ── Motor de Enquadramento CAIXA v1 (09/07/2026) - admin only ───────────────
export interface ParametroNormativo {
  id: string;
  chave: string;
  valor: unknown;
  vigenciaInicio: string;
  vigenciaFim: string | null;
  fonte: string;
  versaoDocumento: string;
  criadoEm: string;
  criadoPor: string | null;
}

export interface ResumoImportacaoMunicipios {
  arquivo: string;
  versaoTabela: string;
  totalLinhas: number;
  inseridos: number;
  novaVigencia: number;
  inalterados: number;
  dormentes: number;
  divergenciasCrossCheck: number;
}

export const parametrosNormativosApi = {
  listar: async (vigentesEm?: string): Promise<ParametroNormativo[]> => {
    const { data } = await api.get<ParametroNormativo[]>('/parametros-normativos', {
      params: vigentesEm ? { vigentesEm } : undefined,
    });
    return data;
  },
  historico: async (chave: string): Promise<ParametroNormativo[]> => {
    const { data } = await api.get<ParametroNormativo[]>(`/parametros-normativos/${encodeURIComponent(chave)}/historico`);
    return data;
  },
  criarNovaVigencia: async (input: { chave: string; valor: unknown; fonte: string; versaoDocumento: string; vigenciaInicio?: string }): Promise<ParametroNormativo> => {
    const { data } = await api.post<ParametroNormativo>('/parametros-normativos', input);
    return data;
  },
};

// ── SPEC_BLOCO3 (14/07/2026) - Parâmetros de INSTITUIÇÃO (banco), família ───
// separada de ParametroNormativo (governo) - EST-040.
export interface ParametroInstituicao {
  id: string;
  banco: BancoPropostaBancaria;
  chave: string;
  valor: unknown;
  vigenciaInicio: string;
  vigenciaFim: string | null;
  fonte: string;
  criadoEm: string;
}

export const parametrosInstituicaoApi = {
  listar: async (vigentesEm?: string): Promise<ParametroInstituicao[]> => {
    const { data } = await api.get<ParametroInstituicao[]>('/parametros-instituicao', {
      params: vigentesEm ? { vigentesEm } : undefined,
    });
    return data;
  },
  historico: async (banco: string, chave: string): Promise<ParametroInstituicao[]> => {
    const { data } = await api.get<ParametroInstituicao[]>(
      `/parametros-instituicao/${encodeURIComponent(banco)}/${encodeURIComponent(chave)}/historico`
    );
    return data;
  },
  criarNovaVigencia: async (input: { banco: string; chave: string; valor: unknown; fonte: string; vigenciaInicio?: string }): Promise<ParametroInstituicao> => {
    const { data } = await api.post<ParametroInstituicao>('/parametros-instituicao', input);
    return data;
  },
};

export const municipiosFgtsApi = {
  importar: async (arquivo: File, versaoTabela?: string): Promise<ResumoImportacaoMunicipios> => {
    const form = new FormData();
    form.append('arquivo', arquivo);
    if (versaoTabela) form.append('versaoTabela', versaoTabela);
    const { data } = await api.post<ResumoImportacaoMunicipios>('/municipios-fgts/importar', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return data;
  },
};

// ── Ocupações (dicionário Anexos V/VI) - SPEC_LAYOUT_UX_V1 (E4) ─────────────
export const ocupacoesApi = {
  listar: async (): Promise<Ocupacao[]> => {
    const { data } = await api.get<Ocupacao[]>('/ocupacoes');
    return data;
  },
};

// ── Simulação CAIXA (extensão assistida) - OTMC-DEC-0006 / OTMC-CMD-0006 ────
// O CRM NÃO simula: a simulação roda na extensão, no navegador do operador
// logado (OTMC-DEC-0005). Estas rotas servem para (1) montar o perfil que
// pré-preenche a tela de revisão da extensão, (2) guardar a captura que voltou e
// (3) comparar simulações pelo contrato normalizado.
export interface PerfilSimulacaoResposta {
  perfil: Record<string, unknown>;
  faltando: string[];
  fontes: Record<string, string>;
  isolamento: string;
}

export interface SimulacaoCaixaRegistro {
  id: string;
  banco: string;
  rotulo: string;
  operadorEmail: string;
  produtoNome: string | null;
  produtoCodigo: string | null;
  produtoVariante: string | null;
  valorFinanciamento: string | null;
  valorEntrada: string | null;
  cotaMaximaPct: string | null;
  primeiraPrestacao: string | null;
  prazoMeses: number | null;
  jurosNominaisPct: string | null;
  jurosEfetivosPct: string | null;
  pdfNomeArquivo: string | null;
  capturadaEm: string;
}

export const simulacaoCaixaApi = {
  estado: async (): Promise<{ simulacaoHabilitada: boolean; statusGatilho: string[]; veiculo: string }> => {
    const { data } = await api.get('/simulacoes-caixa/estado');
    return data;
  },
  perfil: async (operacaoId: string): Promise<PerfilSimulacaoResposta> => {
    const { data } = await api.get<PerfilSimulacaoResposta>(`/simulacoes-caixa/perfil/${operacaoId}`);
    return data;
  },
  listar: async (operacaoId: string): Promise<SimulacaoCaixaRegistro[]> => {
    const { data } = await api.get<SimulacaoCaixaRegistro[]>(`/simulacoes-caixa/operacao/${operacaoId}`);
    return data;
  },
  registrar: async (pacote: Record<string, unknown>): Promise<{ simulacao: SimulacaoCaixaRegistro }> => {
    const { data } = await api.post('/simulacoes-caixa', pacote);
    return data;
  },
};

// ── Templates de mensagem (aba Mensagens) ────────────────────────────────────

export const templatesWhatsappApi = {
  listar: async (): Promise<TemplateWhatsapp[]> => {
    const { data } = await api.get<TemplateWhatsapp[]>('/templates-whatsapp');
    return data;
  },
  criar: async (nome: string, corpo: string): Promise<TemplateWhatsapp> => {
    const { data } = await api.post<TemplateWhatsapp>('/templates-whatsapp', { nome, corpo });
    return data;
  },
};

// ── Central de Comunicação (comentários internos por operação) ───────────────

export const comentariosApi = {
  listar: async (operacaoId: string): Promise<ComentarioOperacao[]> => {
    const { data } = await api.get<ComentarioOperacao[]>(`/operacoes/${operacaoId}/comentarios`);
    return data;
  },
  criar: async (operacaoId: string, corpo: string, mencionados: string[] = []): Promise<ComentarioOperacao> => {
    const { data } = await api.post<ComentarioOperacao>(`/operacoes/${operacaoId}/comentarios`, { corpo, mencionados });
    return data;
  },
  editar: async (operacaoId: string, id: string, corpo: string): Promise<ComentarioOperacao> => {
    const { data } = await api.patch<ComentarioOperacao>(`/operacoes/${operacaoId}/comentarios/${id}`, { corpo });
    return data;
  },
  excluir: async (operacaoId: string, id: string): Promise<void> => {
    await api.delete(`/operacoes/${operacaoId}/comentarios/${id}`);
  },
};

// Usuários mencionáveis (autocomplete de @) - acessível a qualquer autenticado
export const usuariosApi = {
  mencionaveis: async (): Promise<UsuarioMencionavel[]> => {
    const { data } = await api.get<UsuarioMencionavel[]>('/usuarios/mencionaveis');
    return data;
  },
};

// Notificações do usuário logado (menções; destino do sino)
export const notificacoesApi = {
  listar: async (lida?: boolean): Promise<Notificacao[]> => {
    const params = lida === undefined ? {} : { lida: String(lida) };
    const { data } = await api.get<Notificacao[]>('/notificacoes', { params });
    return data;
  },
  contagem: async (): Promise<number> => {
    const { data } = await api.get<{ naoLidas: number }>('/notificacoes/contagem');
    return data.naoLidas;
  },
  marcarLida: async (id: string): Promise<void> => {
    await api.post(`/notificacoes/${id}/lida`);
  },
  lerTodas: async (): Promise<void> => {
    await api.post('/notificacoes/ler-todas');
  },
};

// ── Integrações (Ploomes) ────────────────────────────────────────────────────
export interface PloomesPreview {
  totalPloomes: number; jaImportados: number; jaExistemNoCrm: number; novos: number;
  porOrigem: Record<string, number>; porStatus: Record<string, number>; candidatos: number[];
}
export interface PloomesJob {
  id: string; status: 'rodando' | 'concluido' | 'erro'; total: number; feitos: number; erros: number;
  iniciadoEm: string; terminadoEm?: string; falhas: Array<{ dealId: number; motivo: string }>;
}
export interface PloomesAnexosPreview {
  operacoesHistorico: number; comAnexosImportados: number; semAnexosAinda: number; operacoesIds: string[];
}
export interface PloomesAnexosJob {
  id: string; status: 'rodando' | 'concluido' | 'erro'; totalOperacoes: number; operacoesFeitas: number;
  anexosImportados: number; anexosDedup: number; anexosJa: number; erros: number;
  iniciadoEm: string; terminadoEm?: string; falhaGeral?: string;
}
export const integracoesApi = {
  ploomesPreview: async (): Promise<PloomesPreview> => (await api.get('/integracoes/ploomes/preview')).data,
  ploomesImportar: async (dealIds?: number[]): Promise<{ jobId: string; total: number }> =>
    (await api.post('/integracoes/ploomes/importar', dealIds ? { dealIds } : {})).data,
  ploomesJob: async (jobId: string): Promise<PloomesJob> =>
    (await api.get(`/integracoes/ploomes/importar/${jobId}`)).data,
  // Anexos (Ploomes Blob → SharePoint "Histórico Ploomes/")
  anexosPreview: async (): Promise<PloomesAnexosPreview> => (await api.get('/integracoes/ploomes/anexos/preview')).data,
  anexosImportar: async (incluirMidia?: boolean): Promise<{ jobId: string; total: number }> =>
    (await api.post('/integracoes/ploomes/anexos/importar', { incluirMidia: !!incluirMidia })).data,
  anexosJob: async (jobId: string): Promise<PloomesAnexosJob> =>
    (await api.get(`/integracoes/ploomes/anexos/importar/${jobId}`)).data,
};

// ── Documentos (visão SharePoint: busca + link direto) ───────────────────────
export interface DocumentoItem {
  id: string; nomeArquivo: string; status: string; historicoPloomes: boolean; createdAt: string;
  operacaoId: string; operacaoNumero: string | null; origem: string | null; cliente: string | null;
  caminhoSharePoint: string | null; temLink: boolean;
}
export interface DocumentosPagina { itens: DocumentoItem[]; total: number; page: number; pageSize: number; }
export interface PastaItem { nome: string; graphItemId: string; qtdItens: number | null; }
export interface ArquivoItem { nome: string; graphItemId: string; size: number | null; lastModifiedDateTime: string | null; webUrl: string | null; }
export interface ConteudoPasta { base: string; caminho: string; breadcrumb: string[]; pastas: PastaItem[]; arquivos: ArquivoItem[]; }
export const documentosApi = {
  listar: async (params: { busca?: string; apenasPloomes?: boolean; page?: number; pageSize?: number } = {}): Promise<DocumentosPagina> =>
    (await api.get('/documentos', { params })).data,
  link: async (id: string): Promise<string> => (await api.get(`/documentos/${id}/link`)).data.url,
  pastas: async (caminho?: string): Promise<ConteudoPasta> =>
    (await api.get('/documentos/pastas', { params: caminho ? { caminho } : {} })).data,
};

// ── Extensão de navegador (conector iSolve) ──────────────────────────────────
export interface ExtensaoInfo { slug: string; nome: string; versao: string; descricao: string; }
export interface ExtensaoStatus {
  temToken: boolean; aguardandoRevelacao: boolean; reveladoEm: string | null; prefixo: string | null;
  conectada: boolean; ultimaVersao: string | null; ultimoUsoEm: string | null;
  versaoVigente: string; versaoDesatualizada: boolean;
}
export interface AdminTokenLinha {
  usuarioId: string; nome: string; email: string; papel: string;
  estado: 'sem_token' | 'aguardando' | 'ativo';
  conectada: boolean; nuncaConectou: boolean; geradoEm: string | null;
  ultimaConexaoEm: string | null; ultimaVersao: string | null; versaoDesatualizada: boolean;
}
export const extensaoApi = {
  info: async (): Promise<ExtensaoInfo> => (await api.get('/extensao/info')).data,
  status: async (): Promise<ExtensaoStatus> => (await api.get('/extensao/status')).data,
  revelarToken: async (): Promise<{ token: string; aviso: string }> => (await api.post('/extensao/token/revelar')).data,
  // Admin
  listarTokens: async (): Promise<AdminTokenLinha[]> => (await api.get('/extensao/tokens')).data,
  gerarToken: async (usuarioId: string): Promise<void> => { await api.post(`/extensao/tokens/${usuarioId}/gerar`); },
  revogarToken: async (usuarioId: string): Promise<void> => { await api.delete(`/extensao/tokens/${usuarioId}`); },
};

// ── Board do espelho de pipelines do Ploomes (só-leitura) ─────────────────────
export interface PipelineEstagioBoard { id: string; nome: string; ordem: number; ultimo: boolean; totalOperacoes: number; }
export interface PipelineBoard { id: string; ploomesPipelineId: number; nome: string; ordem: number | null; totalOperacoes: number; estagios: PipelineEstagioBoard[]; }
export interface PipelineCard {
  id: string; numero: string; origem: string; status: string; estagioId: string | null;
  comprador: string; valorFinanciamento: number | null; valorImovel: number | null;
  cidadeImovel: string | null; responsavel: string | null;
  docsAplicaveis: number; docsPendentes: number;
}
export interface PipelineCardsResp { total: number; page: number; pageSize: number; cards: PipelineCard[]; }
export interface TrilhaItem { pipeline: string; estagio: string | null; status: string | null; ploomesDealId: number | null; }

export const pipelinesApi = {
  getBoard: async (): Promise<PipelineBoard[]> => (await api.get<PipelineBoard[]>('/pipelines')).data,
  getCards: async (
    pipelineId: string,
    opts: { estagioId?: string; page?: number; pageSize?: number; q?: string } = {}
  ): Promise<PipelineCardsResp> => {
    const { data } = await api.get<PipelineCardsResp>(`/pipelines/${pipelineId}/cards`, { params: opts });
    return data;
  },
  getTrilha: async (operacaoId: string): Promise<TrilhaItem[]> =>
    (await api.get<TrilhaItem[]>(`/pipelines/operacoes/${operacaoId}/trilha`)).data,

  // Checklist por etapa (2 níveis: grupos → itens) — gate ao mover
  getChecklistOperacao: async (operacaoId: string, estagioId?: string): Promise<ChecklistGrupoView[]> =>
    (await api.get<ChecklistGrupoView[]>(`/pipelines/operacoes/${operacaoId}/checklist`, { params: estagioId ? { estagioId } : {} })).data,
  marcarChecklistItem: async (operacaoId: string, itemId: string, cumprido: boolean, observacao?: string, estagioId?: string): Promise<ChecklistGrupoView[]> =>
    (await api.post<ChecklistGrupoView[]>(`/pipelines/operacoes/${operacaoId}/checklist-item/${itemId}`, { cumprido, observacao, estagioId })).data,
  mover: async (operacaoId: string, estagioDestinoId: string): Promise<{ ok: boolean; estagioAtualId?: string; pipelineAtualId?: string }> =>
    (await api.post(`/pipelines/operacoes/${operacaoId}/mover`, { estagioDestinoId })).data,

  // Parametrização (admin) — grupos e itens
  listarChecklistsEstagio: async (estagioId: string): Promise<ChecklistGrupoParam[]> =>
    (await api.get<ChecklistGrupoParam[]>(`/pipelines/estagios/${estagioId}/checklists`)).data,
  criarGrupo: async (data: { estagioPipelineId: string; nome: string; origemAplicavel?: string | null; textoInfo?: string | null; ordem?: number }): Promise<ChecklistGrupoParam> =>
    (await api.post<ChecklistGrupoParam>('/pipelines/checklists', data)).data,
  atualizarGrupo: async (id: string, data: Partial<ChecklistGrupoParam>): Promise<ChecklistGrupoParam> =>
    (await api.patch<ChecklistGrupoParam>(`/pipelines/checklists/${id}`, data)).data,
  excluirGrupo: async (id: string): Promise<void> => { await api.delete(`/pipelines/checklists/${id}`); },
  criarItem: async (data: { checklistEtapaId: string; nome: string; obrigatorio?: boolean; ordem?: number }): Promise<ChecklistItemParam> =>
    (await api.post<ChecklistItemParam>('/pipelines/checklist-itens', data)).data,
  atualizarItem: async (id: string, data: Partial<ChecklistItemParam>): Promise<ChecklistItemParam> =>
    (await api.patch<ChecklistItemParam>(`/pipelines/checklist-itens/${id}`, data)).data,
  excluirItem: async (id: string): Promise<void> => { await api.delete(`/pipelines/checklist-itens/${id}`); },

  // Esteiras/estágios (admin) — CRM é dono da estrutura
  pipelineDetalhe: async (id: string): Promise<PipelineDetalhe> => (await api.get<PipelineDetalhe>(`/pipelines/${id}/detalhe`)).data,
  criarPipeline: async (data: { nome: string; cor?: string | null; icone?: string | null }): Promise<{ id: string }> => (await api.post('/pipelines', data)).data,
  atualizarPipeline: async (id: string, data: { nome?: string; cor?: string | null; icone?: string | null; ordem?: number; ativo?: boolean }): Promise<unknown> => (await api.patch(`/pipelines/${id}`, data)).data,
  excluirPipeline: async (id: string): Promise<void> => { await api.delete(`/pipelines/${id}`); },
  arquivarPipeline: async (id: string): Promise<void> => { await api.post(`/pipelines/${id}/arquivar`); },
  duplicarPipeline: async (id: string): Promise<{ id: string; nome: string }> => (await api.post(`/pipelines/${id}/duplicar`)).data,
  criarEstagio: async (data: { pipelineId: string; nome: string; ultimo?: boolean }): Promise<PipelineEstagioDetalhe> => (await api.post('/pipelines/estagios', data)).data,
  atualizarEstagio: async (id: string, data: { nome?: string; ultimo?: boolean; ativo?: boolean }): Promise<PipelineEstagioDetalhe> => (await api.patch(`/pipelines/estagios/${id}`, data)).data,
  excluirEstagio: async (id: string): Promise<void> => { await api.delete(`/pipelines/estagios/${id}`); },
  reordenarEstagios: async (pipelineId: string, ordem: string[]): Promise<PipelineDetalhe> => (await api.post(`/pipelines/${pipelineId}/estagios/reordenar`, { ordem })).data,
};

export interface PipelineEstagioDetalhe { id: string; nome: string; ordem: number; ultimo: boolean; ativo: boolean; ploomesStageId: number | null; }
export interface PipelineDetalhe { id: string; nome: string; cor: string | null; icone: string | null; ordem: number | null; ativo: boolean; ploomesPipelineId: number | null; estagios: PipelineEstagioDetalhe[]; }

export interface ChecklistItemView { id: string; nome: string; ordem: number; obrigatorio: boolean; ploomesFieldKey: string | null; cumprido: boolean; observacao: string | null; }
export interface ChecklistGrupoView { id: string; nome: string; textoInfo: string | null; origemAplicavel: string | null; itens: ChecklistItemView[]; }
export interface ChecklistItemParam { id: string; nome: string; ordem: number; obrigatorio: boolean; ativo: boolean; ploomesFieldKey: string | null; }
export interface ChecklistGrupoParam { id: string; nome: string; ordem: number; origemAplicavel: string | null; textoInfo: string | null; ativo: boolean; ploomesChecklistId: number | null; itens: ChecklistItemParam[]; }

// ── Helpers ──────────────────────────────────────────────────────────────────

function agruparPorEsteira(ops: Operacao[]): ColunasKanban {
  const cols: ColunasKanban = { 0: [], 1: [], 2: [], 3: [] };
  ops.forEach((op) => {
    if (op.esteira >= 0 && op.esteira <= 3) {
      cols[op.esteira as keyof ColunasKanban].push(op);
    }
  });
  return cols;
}

export default api;
